import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  Space,
  Typography,
  message,
  Spin,
  Input,
  Checkbox
} from 'antd';
import {
  SearchOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import { trIncludes } from '../../utils/trText';
import axios from 'axios';
import './YetkiYonetimPage.css';

const { Text } = Typography;

const YetkiYonetimPage = () => {
  const { darkMode } = useTheme();
  const { getAuthHeaders } = useAuth();
  const [yetkiler, setYetkiler] = useState([]);
  const [pozisyonlar, setPozisyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [formData, setFormData] = useState({});
  const [searchText, setSearchText] = useState('');

  const saveTimersRef = useRef({});
  const tableRef = useRef(null);
  // Kutucuk ipucu: ~13 bin hücreye ayrı Tooltip yerine tek kutu, fare işleyicisi DOM'dan günceller
  const ipucuRef = useRef(null);

  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const [yetkilerRes, pozisyonlarRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/yetki/list`, { headers }),
        axios.get(`${API_BASE_URL}/yetki/pozisyonlar`, { headers })
      ]);

      if (yetkilerRes.data.success && pozisyonlarRes.data.success) {
        setYetkiler(yetkilerRes.data.yetkiler);
        setPozisyonlar(pozisyonlarRes.data.pozisyonlar);

        const initialData = {};
        yetkilerRes.data.yetkiler.forEach(yetki => {
          const readIds = (yetki.r || []).map(id => parseInt(id, 10));
          const writeIds = (yetki.w || []).map(id => parseInt(id, 10));

          initialData[yetki.id] = {
            read: new Set(readIds),
            write: new Set(writeIds),
            herkes_r: yetki.herkes_r || false,
            herkes_w: yetki.herkes_w || false,
            aciklama: yetki.aciklama || ''
          };
        });
        setFormData(initialData);
      }
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      message.error('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCheckboxChange = useCallback((yetkiId, type, pozisyonId = null) => {
    setFormData(prev => {
      const current = prev[yetkiId] || {
        read: new Set(),
        write: new Set(),
        herkes_r: false,
        herkes_w: false,
        aciklama: ''
      };

      let updated;
      if (type === 'herkes_r') {
        updated = { ...current, herkes_r: !current.herkes_r };
      } else if (type === 'herkes_w') {
        updated = { ...current, herkes_w: !current.herkes_w };
      } else if (type === 'read' && pozisyonId !== null) {
        const newRead = new Set(current.read);
        if (newRead.has(pozisyonId)) {
          newRead.delete(pozisyonId);
        } else {
          newRead.add(pozisyonId);
        }
        updated = { ...current, read: newRead };
      } else if (type === 'write' && pozisyonId !== null) {
        const newWrite = new Set(current.write);
        if (newWrite.has(pozisyonId)) {
          newWrite.delete(pozisyonId);
        } else {
          newWrite.add(pozisyonId);
        }
        updated = { ...current, write: newWrite };
      } else {
        return prev;
      }

      return { ...prev, [yetkiId]: updated };
    });

    if (saveTimersRef.current[yetkiId]) {
      clearTimeout(saveTimersRef.current[yetkiId]);
    }

    saveTimersRef.current[yetkiId] = setTimeout(async () => {
      const currentData = formData[yetkiId];
      if (!currentData) return;

      try {
        setSaving(prev => ({ ...prev, [yetkiId]: true }));
        const headers = getAuthHeaders();

        await axios.post(
          `${API_BASE_URL}/yetki/update`,
          {
            id: yetkiId,
            read: Array.from(currentData.read),
            write: Array.from(currentData.write),
            herkes_r: currentData.herkes_r || false,
            herkes_w: currentData.herkes_w || false,
            aciklama: currentData.aciklama || ''
          },
          { headers }
        );

        message.success('Yetki güncellendi', 1);
      } catch (error) {
        console.error('Kaydetme hatası:', error);
        message.error('Hata oluştu');
        loadData();
      } finally {
        setSaving(prev => ({ ...prev, [yetkiId]: false }));
      }
    }, 500);
  }, [formData, getAuthHeaders, loadData]);

  const filteredYetkiler = useMemo(() => {
    if (!searchText) return yetkiler;
    const search = searchText.trim();
    return yetkiler.filter(yetki => (
      trIncludes(yetki.anahtar, search) ||
      trIncludes(yetki.tip, search)
    ));
  }, [yetkiler, searchText]);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    let currentHoveredColumn = null;

    const ipucuGoster = (td) => {
      const kutu = ipucuRef.current;
      if (!kutu) return;
      const metin = td && td.dataset.ipucu;
      if (!metin) { kutu.style.display = 'none'; return; }
      const r = td.getBoundingClientRect();
      kutu.textContent = metin;
      kutu.style.display = 'block';
      const ust = r.top - kutu.offsetHeight - 6;
      kutu.style.top = `${ust < 4 ? r.bottom + 6 : ust}px`;
      kutu.style.left = `${Math.max(8, Math.min(r.left + r.width / 2 - kutu.offsetWidth / 2, window.innerWidth - kutu.offsetWidth - 8))}px`;
    };

    const handleMouseOver = (e) => {
      const td = e.target.closest('td');
      if (!td || td.closest('thead')) return;
      ipucuGoster(td);

      const row = td.parentElement;
      const colIndex = Array.from(row.children).indexOf(td);
      if (colIndex === 0 || colIndex === currentHoveredColumn) return;

      if (currentHoveredColumn !== null) {
        const rows = table.querySelectorAll('tbody tr');
        rows.forEach(r => {
          const cell = r.children[currentHoveredColumn];
          if (cell) cell.classList.remove('column-hovered');
        });
      }

      currentHoveredColumn = colIndex;
      const rows = table.querySelectorAll('tbody tr');
      rows.forEach(r => {
        const cell = r.children[colIndex];
        if (cell) cell.classList.add('column-hovered');
      });
    };

    const handleMouseOut = (e) => {
      if (!table.contains(e.relatedTarget)) {
        ipucuGoster(null);
        if (currentHoveredColumn !== null) {
          const rows = table.querySelectorAll('tbody tr');
          rows.forEach(r => {
            const cell = r.children[currentHoveredColumn];
            if (cell) cell.classList.remove('column-hovered');
          });
          currentHoveredColumn = null;
        }
      }
    };

    table.addEventListener('mouseover', handleMouseOver);
    table.addEventListener('mouseout', handleMouseOut);

    return () => {
      table.removeEventListener('mouseover', handleMouseOver);
      table.removeEventListener('mouseout', handleMouseOut);
    };
  }, [yetkiler, pozisyonlar]); 

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={`yetki-yonetim-page ${darkMode ? 'dark' : 'light'}`}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Input
            placeholder="Yetki ara..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400 }}
            allowClear
          />
          
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
            <table 
              ref={tableRef}
              className={`yetki-native-table ${darkMode ? 'dark' : ''}`} 
              cellSpacing="0"
            >
              <thead>
                <tr>
                  <th style={{ minWidth: 150, position: 'sticky', left: 0, zIndex: 10 }}>Pozisyon</th>
                  {filteredYetkiler.map(yetki => (
                    <React.Fragment key={yetki.id}>
                      <th style={{ 
                        width: 50, 
                        writingMode: 'vertical-rl', 
                        textOrientation: 'mixed',
                        fontSize: '9px',
                        padding: '8px 4px'
                      }}>
                        {yetki.tip}
                      </th>
                      <th style={{ 
                        width: 50, 
                        writingMode: 'vertical-rl', 
                        textOrientation: 'mixed',
                        fontSize: '9px',
                        padding: '8px 4px'
                      }}>
                        {yetki.anahtar}
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Herkes satırı */}
                <tr>
                  <td style={{ 
                    textAlign: 'left', 
                    position: 'sticky', 
                    left: 0, 
                    background: darkMode ? '#141414' : '#fff', 
                    zIndex: 5 
                  }}>
                    <Text strong style={{ fontSize: '11px' }}>Herkes</Text>
                  </td>
                  {filteredYetkiler.map((yetki) => {
                    const data = formData[yetki.id] || { read: new Set(), write: new Set(), herkes_r: false, herkes_w: false };
                    const isLoading = saving[yetki.id];
                    return (
                      <React.Fragment key={yetki.id}>
                        <td
                          data-ipucu={`Herkes · ${yetki.tip} / ${yetki.anahtar} · Okuma`}
                          style={{
                            textAlign: 'center',
                            background: data.herkes_r ? '#ffcaca' : '',
                            padding: '4px'
                          }}
                        >
                          <Checkbox
                            checked={data.herkes_r}
                            onChange={() => handleCheckboxChange(yetki.id, 'herkes_r')}
                            disabled={isLoading}
                            style={{ fontSize: '11px' }}
                          />
                          {isLoading && <Spin size="small" style={{ marginLeft: 4 }} />}
                        </td>
                        <td
                          data-ipucu={`Herkes · ${yetki.tip} / ${yetki.anahtar} · Yazma`}
                          style={{
                            textAlign: 'center',
                            background: data.herkes_w ? '#ffcaca' : '',
                            padding: '4px'
                          }}
                        >
                          <Checkbox
                            checked={data.herkes_w}
                            onChange={() => handleCheckboxChange(yetki.id, 'herkes_w')}
                            disabled={isLoading}
                            style={{ fontSize: '11px' }}
                          />
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
                {/* Pozisyon satırları */}
                {pozisyonlar.map(poz => (
                  <tr key={poz.id}>
                    <td style={{ 
                      textAlign: 'left', 
                      position: 'sticky', 
                      left: 0, 
                      background: darkMode ? '#141414' : '#fff', 
                      zIndex: 5 
                    }}>
                      <Text style={{ fontSize: '11px' }}>{poz.ad}</Text>
                    </td>
                    {filteredYetkiler.map((yetki) => {
                      const data = formData[yetki.id] || { read: new Set(), write: new Set(), herkes_r: false, herkes_w: false };
                      const isLoading = saving[yetki.id];
                      const hasRead = data.read.has(poz.id);
                      const hasWrite = data.write.has(poz.id);
                      return (
                        <React.Fragment key={yetki.id}>
                          <td data-ipucu={`${poz.ad} · ${yetki.tip} / ${yetki.anahtar} · Okuma`} style={{ textAlign: 'center', padding: '4px' }}>
                            <Checkbox
                              checked={hasRead}
                              onChange={() => handleCheckboxChange(yetki.id, 'read', poz.id)}
                              disabled={isLoading}
                              style={{ fontSize: '11px' }}
                            />
                          </td>
                          <td data-ipucu={`${poz.ad} · ${yetki.tip} / ${yetki.anahtar} · Yazma`} style={{ textAlign: 'center', padding: '4px' }}>
                            <Checkbox
                              checked={hasWrite}
                              onChange={() => handleCheckboxChange(yetki.id, 'write', poz.id)}
                              disabled={isLoading}
                              style={{ fontSize: '11px' }}
                            />
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Space>
      </Card>
      <div ref={ipucuRef} className="yetki-ipucu" role="tooltip" />
    </div>
  );
};

export default YetkiYonetimPage;